import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TextEditor } from '../components/Editor';
import { useEditorStore } from '../stores/editorStore';
import { analysisApi, exportApi } from '../services/api';

const HomePage: React.FC = () => {
  const {
    originalText,
    analysis,
    edits,
    appliedEdits,
    isAnalyzing,
    setOriginalText,
    setAnalysis,
    setIsAnalyzing,
    acceptAll,
    reset,
    undo,
    redo,
  } = useEditorStore();

  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // Analysis mutation
  const analyzeMutation = useMutation({
    mutationFn: (text: string) => analysisApi.analyze(text),
    onMutate: () => {
      setIsAnalyzing(true);
    },
    onSuccess: (response) => {
      setAnalysis(response.data);
      setIsAnalyzing(false);
    },
    onError: (error: any) => {
      setIsAnalyzing(false);
      alert(error.response?.data?.detail || 'Analysis failed');
    },
  });

  const handleAnalyze = () => {
    if (!originalText.trim()) {
      alert('Please enter some text to analyze');
      return;
    }
    analyzeMutation.mutate(originalText);
  };

  const handleExport = async (format: 'txt' | 'docx' | 'pdf') => {
    if (!analysis) return;

    try {
      let response;
      if (format === 'txt') {
        response = await exportApi.exportTxt(analysis.id);
      } else if (format === 'docx') {
        response = await exportApi.exportDocx(analysis.id);
      } else {
        response = await exportApi.exportPdf(analysis.id);
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `grammarhug_analysis_${analysis.id}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Export failed');
    }

    setExportMenuOpen(false);
  };

  const handleCopyToClipboard = async () => {
    const textToCopy = analysis?.corrected_text || originalText;
    await navigator.clipboard.writeText(textToCopy);
    alert('Copied to clipboard');
  };

  const unappliedCount = edits.length - appliedEdits.size;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Text Editor
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Enter your text and click Check to analyze
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-3">
          {analysis && (
            <>
              <button onClick={undo} className="btn-ghost p-2" title="Undo">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </button>
              <button onClick={redo} className="btn-ghost p-2" title="Redo">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                </svg>
              </button>
              <button onClick={reset} className="btn-secondary">
                Clear
              </button>
            </>
          )}
          <button
            onClick={handleAnalyze}
            disabled={isAnalyzing || !originalText.trim()}
            className="btn-primary"
          >
            {isAnalyzing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Analyzing...
              </>
            ) : (
              'Check'
            )}
          </button>
        </div>
      </div>

      {/* Editor card */}
      <div className="card">
        <div className="p-6">
          <TextEditor />
        </div>

        {/* Footer with stats and actions */}
        {analysis && (
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Stats */}
              <div className="flex items-center space-x-4 text-sm">
                <span className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">{edits.length}</span> corrections found
                </span>
                {unappliedCount > 0 && (
                  <span className="text-orange-600 dark:text-orange-400">
                    <span className="font-semibold">{unappliedCount}</span> remaining
                  </span>
                )}
                {analysis.processing_time_ms && (
                  <span className="text-gray-500 dark:text-gray-400">
                    Processed in {analysis.processing_time_ms}ms
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                {unappliedCount > 0 && (
                  <button onClick={acceptAll} className="btn-secondary text-sm">
                    Accept All
                  </button>
                )}
                <button onClick={handleCopyToClipboard} className="btn-secondary text-sm">
                  Copy
                </button>
                <div className="relative">
                  <button
                    onClick={() => setExportMenuOpen(!exportMenuOpen)}
                    className="btn-secondary text-sm"
                  >
                    Export
                    <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {exportMenuOpen && (
                    <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                      <button
                        onClick={() => handleExport('txt')}
                        className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Plain Text (.txt)
                      </button>
                      <button
                        onClick={() => handleExport('docx')}
                        className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Word (.docx)
                      </button>
                      <button
                        onClick={() => handleExport('pdf')}
                        className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        PDF (.pdf)
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error codes summary */}
      {analysis && Object.keys(analysis.error_codes).length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Error Summary
          </h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(analysis.error_codes)
              .sort((a, b) => b[1] - a[1])
              .map(([code, count]) => (
                <span
                  key={code}
                  className="px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                >
                  {code}: {count}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
